// Consolidado da Fiscalização (#404). Somente leitura; filtros de período
// (dias) e posto na URL. A definição dos números mora no backend
// (services/paineis/fiscalizacao.js + montaPainelFiscalizacao.js); esta tela
// só desenha e exporta.
import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBBtn, MDBSpinner, MDBIcon } from 'mdbreact';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

import NavBar from '../../../components/NavBar';
import api from '../../../services/api';
import {
  KpiTile, ListaBarras, NotaMetodologica, formataNumero, formataReais, formataReaisCheio, formataDataHora,
} from '../../../components/Painel';
import {
  filtrosDaUrl, paramsDosFiltros, queryDosFiltros, limpaCampo, rotuloPeriodo, formataQuantidade, formataQuantidades,
  formataCnpj, rotuloInfracao, nomeDoArquivo, abasDaPlanilha, avisoReceita, textoVinculo, opcoesDePosto,
  notaSemAutos, notaForaDosPostos,
} from './consolidado';

const NOTA = [
  {
    icone: 'filter',
    titulo: 'Recorte',
    texto: 'Abordagens ativas do módulo Gestão de Ocorrências, pela data da abordagem (horário de Brasília) e pela gerência de lotação de quem registrou. "Todos os postos" é a operação inteira, Núcleo de Operação Volante incluído; um posto recorta só aquela gerência.',
  },
  {
    icone: 'truck',
    titulo: 'Veículos',
    texto: 'Por tipo: cada veículo de cada abordagem (registros) e as placas distintas. Cada placa é contada uma vez, com a quantidade de abordagens que teve no período — as que mais se repetem aparecem na tela; a lista inteira vai na planilha.',
  },
  {
    icone: 'users',
    titulo: 'Condutores e ajudantes',
    texto: (
      <>
        <strong>Condutor</strong> é o abordado do tipo Condutor. <strong>Ajudante</strong> é todo outro abordado do
        veículo: Chapa, Carona, Passageiro, Parentes e Cônjuges. &quot;Marcados como condutor&quot; é a caixa de quem
        dirigia, preenchida à parte — há tipo Condutor sem a caixa e ajudante com ela.
      </>
    ),
  },
  {
    icone: 'building',
    titulo: 'Empresas fiscalizadas',
    texto: 'Cada CNPJ contado uma vez no período, com a quantidade de abordagens em que apareceu. Abordagem sem empresa informada não entra.',
  },
  {
    icone: 'gavel',
    titulo: 'Infrações',
    texto: 'Infração aplicada é a ocorrência autuada (a mesma regra dos dashboards), separada em fiscal e policial. As ocorrências registradas sem autuação aparecem só como contexto. Por objeto: grupo (nível 1) e item (nível 2); uma infração com dois itens do mesmo grupo conta uma vez no grupo.',
  },
  {
    icone: 'balance-scale',
    titulo: 'Quantidade',
    texto: 'É a quantidade da mercadoria lançada na infração, na unidade em que foi lançada — não é a carga total do veículo. Unidades diferentes não se somam: kg e litro aparecem em linhas separadas.',
  },
  {
    icone: 'money',
    titulo: 'Valores dos autos (Receita)',
    texto: (
      <>
        Os valores vêm da base de autos de infração da Receita Estadual, pela <strong>data de lavratura</strong> e
        pelo RF de lavratura, importada todo início de mês. Em &quot;todos os postos&quot;, o total inclui a
        <strong> Auditoria Especializada</strong> e RFs fora dos postos (&quot;Outros&quot;), que não são posto do
        SIG — a tabela por origem mostra quanto vem de cada um. Angra dos Reis e Timbó não recebem auto pelo próprio
        RF há anos: os autos de lá, se houver, estão no RF de outro posto ou da Auditoria. O único elo com a infração do SIG é o campo &quot;Nº
        (Autuação / RO)&quot;, que só casa com um auto quando o agente digita o número do auto — a tela mostra quantas
        casam. Na maioria das vezes o campo traz o <strong>número do processo</strong>, que a base da Receita não
        informa. Por isso não há valor por objeto: os dois blocos ficam lado a lado.
      </>
    ),
  },
];

// Impressão (Ctrl+P ou o botão "Imprimir / PDF"). Na tela as tabelas têm
// rolagem própria; impressas, a rolagem cortava as linhas que não cabiam. Aqui
// a altura máxima sai, o menu, os filtros e os botões somem, e os títulos das
// tabelas se repetem a cada página. Vai num <style> dentro do componente — e não
// num .css importado — para valer só enquanto esta tela está aberta.
const CSS_IMPRESSAO = `
@media print {
  @page { size: A4 portrait; margin: 12mm; }
  html, body { background: #fff !important; }
  .fixed-sn, .navbar, .side-nav, .modal, .modal-backdrop { display: none !important; }
  .consolidado-fiscalizacao { max-width: none !important; width: 100% !important; margin: 0 !important; padding: 0 !important; }
  .consolidado-fiscalizacao .table-responsive { max-height: none !important; overflow: visible !important; }
  .consolidado-fiscalizacao table { font-size: 9pt !important; }
  .consolidado-fiscalizacao thead { display: table-header-group; }
  .consolidado-fiscalizacao tr { break-inside: avoid; }
  .consolidado-fiscalizacao .card { box-shadow: none !important; border: 1px solid #ccc !important; break-inside: avoid; }
  .consolidado-fiscalizacao .card.pode-quebrar { break-inside: auto; }
  .consolidado-fiscalizacao h6 { break-after: avoid; }
  .consolidado-fiscalizacao .card-body { padding: .6rem .8rem !important; }
  .consolidado-fiscalizacao .so-impressao { display: block !important; }
  .consolidado-fiscalizacao a { color: inherit !important; text-decoration: none !important; }
  .consolidado-fiscalizacao * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;

const agoraBR = () => new Date().toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

const tabela = { fontSize: '.88rem' };
const rolagem = { maxHeight: 420, overflowY: 'auto' };
const alerta = { color: 'var(--painel-alerta)' };

const Tabela = ({ colunas, linhas, rodape }) => (
  <div className="table-responsive" style={rolagem}>
    <table className="table table-sm mb-0" style={tabela}>
      <thead>
        <tr>{colunas.map(([rotulo, , num]) => <th key={rotulo} className={num ? 'text-right text-nowrap' : ''}>{rotulo}</th>)}</tr>
      </thead>
      <tbody>
        {linhas.length === 0 && (
          <tr><td colSpan={colunas.length} className="text-muted">Nenhum registro no período.</td></tr>
        )}
        {linhas.map((l, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={i}>
            {colunas.map(([rotulo, valor, num]) => (
              <td key={rotulo} className={num ? 'text-right text-nowrap' : ''}>{valor(l)}</td>
            ))}
          </tr>
        ))}
      </tbody>
      {rodape && linhas.length > 1 && (
        <tfoot>
          <tr className="font-weight-bold">
            {colunas.map(([rotulo, , num], i) => (
              <td key={rotulo} className={num ? 'text-right text-nowrap' : ''}>{rodape[i]}</td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);

// Objeto em dois níveis: linha do grupo em negrito, itens recuados abaixo.
const TabelaObjetos = ({ grupos }) => (
  <div className="table-responsive" style={{ ...rolagem, maxHeight: 560 }}>
    <table className="table table-sm mb-0" style={tabela}>
      <thead>
        <tr>
          <th>Grupo / objeto</th>
          <th className="text-right text-nowrap">Infrações</th>
          <th className="text-right">Quantidade lançada</th>
        </tr>
      </thead>
      <tbody>
        {grupos.length === 0 && <tr><td colSpan={3} className="text-muted">Nenhuma infração com objeto no período.</td></tr>}
        {grupos.map((g) => (
          <Fragment key={g.grupo}>
            <tr style={{ background: 'var(--painel-fundo-suave, rgba(0,0,0,.035))' }}>
              <td className="font-weight-bold">{g.grupo}</td>
              <td className="text-right text-nowrap font-weight-bold">{formataNumero(g.ocorrencias)}</td>
              <td className="text-right font-weight-bold">{formataQuantidades(g.quantidades)}</td>
            </tr>
            {g.itens.map((it) => (
              <tr key={`${g.grupo}|${it.item}|${it.unidade}`}>
                <td style={{ paddingLeft: '1.75rem' }}>{it.item}</td>
                <td className="text-right text-nowrap">{formataNumero(it.ocorrencias)}</td>
                <td className="text-right text-nowrap">{`${formataQuantidade(it.quantidade)} ${it.unidade}`}</td>
              </tr>
            ))}
          </Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

export default function PainelFiscalizacao() {
  const history = useHistory();
  const location = useLocation();
  const filtros = filtrosDaUrl(location.search);
  const [rascunho, setRascunho] = useState(filtros);
  const [dados, setDados] = useState(null);
  const [postos, setPostos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [erro, setErro] = useState(null); // { tipo: 'sem-acesso' | 'invalido' | 'falha', texto }

  useEffect(() => {
    setRascunho(filtros);
    let vivo = true;
    setCarregando(true);
    setErro(null);
    api.get('/paineis/fiscalizacao', { params: paramsDosFiltros(filtros) })
      .then(({ data }) => { if (vivo) { setDados(data); setPostos(data.postos || []); } })
      .catch((e) => {
        if (!vivo) return;
        const status = e && e.response && e.response.status;
        setDados(null);
        if (status === 403) setErro({ tipo: 'sem-acesso' });
        else if (status === 400) {
          setErro({ tipo: 'invalido', texto: e.response.data.erro });
          setRascunho(limpaCampo(filtros, e.response.data.campo));
        } else setErro({ tipo: 'falha' });
      })
      .finally(() => vivo && setCarregando(false));
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state]);

  const aplicar = () => history.push({ pathname: location.pathname, search: queryDosFiltros(rascunho) });
  const atualizar = () =>
    history.replace({ pathname: location.pathname, search: location.search, state: { t: Date.now() } });

  // A planilha leva as listas completas de placas e empresas, que a tela não
  // carrega. Os filtros são os do painel exibido, não os do rascunho.
  const exportar = async () => {
    setExportando(true);
    try {
      const params = { de: dados.periodo.de, ate: dados.periodo.ate, ...(dados.posto ? { posto: dados.posto } : {}) };
      const { data: listas } = await api.get('/paineis/fiscalizacao/listas', { params });
      const wb = XLSX.utils.book_new();
      abasDaPlanilha(dados, listas).forEach(([nome, linhas]) => {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhas.length ? linhas : [{}]), nome);
      });
      XLSX.writeFile(wb, nomeDoArquivo(dados));
    } catch (e) {
      toast.error('Não foi possível gerar a planilha. Tente de novo.');
    } finally {
      setExportando(false);
    }
  };

  const campoData = (campo, rotulo) => (
    <label className="mb-0" style={{ fontSize: '.85rem' }}>
      {rotulo}
      <input
        type="date"
        min="2021-01-01"
        className="form-control form-control-sm"
        value={rascunho[campo] || (dados ? dados.periodo[campo] : '')}
        onChange={(e) => setRascunho({ ...rascunho, [campo]: e.target.value })}
      />
    </label>
  );

  const filtrosForm = (
    <div className="d-flex flex-wrap align-items-end mb-3" style={{ gap: '.75rem' }}>
      {campoData('de', 'De')}
      {campoData('ate', 'Até')}
      <label className="mb-0" style={{ fontSize: '.85rem' }}>
        Posto
        <select
          className="browser-default custom-select custom-select-sm"
          value={rascunho.posto}
          onChange={(e) => setRascunho({ ...rascunho, posto: e.target.value })}
        >
          <option value="">Todos</option>
          {opcoesDePosto(postos, rascunho.posto).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </label>
      <button type="button" className="painel-acao" onClick={aplicar}>
        <MDBIcon icon="filter" />
        Aplicar
      </button>
    </div>
  );

  const corpo = () => {
    if (carregando && !dados) return <div className="text-center my-5"><MDBSpinner /></div>;
    if (erro && erro.tipo === 'sem-acesso') return <p className="my-5 text-center">Você não tem acesso a este painel.</p>;
    if (erro && erro.tipo === 'invalido') return <p className="my-5 text-center">{erro.texto}</p>;
    if (erro) {
      return <div className="text-center my-5"><MDBBtn color="primary" onClick={atualizar}>Tentar de novo</MDBBtn></div>;
    }

    const { veiculos: v, abordados: a, empresas: e, infracoes: inf, autos_receita: rec } = dados;
    const kpis = [
      { valor: dados.abordagens, rotulo: 'abordagens' },
      {
        valor: v.placas_distintas,
        rotulo: 'veículos (placas distintas)',
        detalhe: `${formataNumero(v.total)} registros · ${formataNumero(v.placas_repetidas)} placas repetidas`,
      },
      {
        valor: a.condutores + a.ajudantes,
        rotulo: 'condutores e ajudantes (registros)',
        detalhe: `${formataNumero(a.condutores)} condutores · ${formataNumero(a.ajudantes)} ajudantes`
          + ` · ${formataNumero(a.cpfs_distintos)} pessoas distintas (CPF)`,
      },
      {
        valor: e.cnpjs_distintos,
        rotulo: 'empresas fiscalizadas',
        detalhe: `${formataNumero(e.cnpjs_repetidos)} abordadas mais de uma vez`,
      },
      {
        valor: inf.autuadas,
        rotulo: 'infrações aplicadas',
        detalhe: `${formataNumero(inf.fiscais)} fiscais · ${formataNumero(inf.policiais)} policiais`,
      },
    ];

    return (
      <>
        {dados.periodo.parcial && (
          <p className="small mb-2" style={alerta}>
            <MDBIcon icon="exclamation-triangle" className="mr-1" />
            O período chega até hoje: os números ainda vão mudar com os registros do dia.
          </p>
        )}
        <div className="row mb-3">
          {kpis.map((k) => (
            <div key={k.rotulo} className="col-lg col-md-4 col-sm-6 mb-2">
              <KpiTile valor={k.valor} rotulo={k.rotulo} detalhe={k.detalhe} compacto />
            </div>
          ))}
        </div>

        <MDBRow className="mb-3">
          <MDBCol md="6" className="mb-2">
            <MDBCard className="h-100"><MDBCardBody>
              <h6 className="mb-3">Veículos abordados por tipo</h6>
              <Tabela
                colunas={[
                  ['Tipo', (l) => l.tipo],
                  ['Registros', (l) => formataNumero(l.veiculos), true],
                  ['Placas distintas', (l) => formataNumero(l.placas), true],
                ]}
                linhas={v.por_tipo}
                rodape={['Total', formataNumero(v.total), formataNumero(v.placas_distintas)]}
              />
              <small className="text-muted d-block mt-2">
                O total de placas distintas não é a soma da coluna: a mesma placa pode aparecer com tipos diferentes.
                {v.sem_placa > 0 && ` ${formataNumero(v.sem_placa)} registro(s) sem placa.`}
              </small>
            </MDBCardBody></MDBCard>
          </MDBCol>
          <MDBCol md="6" className="mb-2">
            <MDBCard className="h-100"><MDBCardBody>
              <h6 className="mb-1">Placas mais abordadas no período</h6>
              <p className="small text-muted mb-3">
                {formataNumero(v.placas_repetidas)} de {formataNumero(v.placas_distintas)} placas foram abordadas mais de uma
                vez. A lista completa vai na planilha.
              </p>
              <Tabela
                colunas={[
                  ['Placa', (l) => l.placa],
                  ['Tipo', (l) => l.tipos],
                  ['Abordagens', (l) => formataNumero(l.abordagens), true],
                ]}
                linhas={v.mais_abordadas}
              />
            </MDBCardBody></MDBCard>
          </MDBCol>
        </MDBRow>

        <MDBRow className="mb-3">
          <MDBCol md="6" className="mb-2">
            <MDBCard className="h-100"><MDBCardBody>
              <h6 className="mb-3">Condutores e ajudantes</h6>
              <Tabela
                colunas={[
                  ['Tipo', (l) => l.tipo],
                  ['Papel', (l) => (l.condutor ? 'Condutor' : 'Ajudante')],
                  ['Abordados', (l) => formataNumero(l.abordados), true],
                  ['Marcados como condutor', (l) => formataNumero(l.dirigindo), true],
                ]}
                linhas={a.por_tipo}
                rodape={['Total', '', formataNumero(a.total), formataNumero(a.dirigindo)]}
              />
              <small className="text-muted d-block mt-2">
                Contagem por registro: a mesma pessoa abordada duas vezes conta 2. Pelo CPF, são{' '}
                {formataNumero(a.cpfs_distintos)} pessoas distintas.
              </small>
              <small className="text-muted d-block mt-1">
                O formulário de abordagem registra basicamente o condutor: ajudantes quase nunca são lançados, então o número
                baixo de ajudantes não quer dizer que os veículos circulam sem eles.
              </small>
            </MDBCardBody></MDBCard>
          </MDBCol>
          <MDBCol md="6" className="mb-2">
            <MDBCard className="h-100"><MDBCardBody>
              <h6 className="mb-1">Empresas mais fiscalizadas no período</h6>
              <p className="small text-muted mb-3">
                {formataNumero(e.cnpjs_distintos)} empresas, cada uma contada uma vez; {formataNumero(e.cnpjs_repetidos)} aparecem
                em mais de uma abordagem. {formataNumero(e.abordagens_com_empresa)} das {formataNumero(dados.abordagens)} abordagens
                têm empresa informada. A lista completa vai na planilha.
                {' '}O formulário não diz se a empresa é transportadora, remetente ou destinatária
                {e.cpfs_na_lista > 0 && `, e ${formataNumero(e.cpfs_na_lista)} dos documentos têm 11 dígitos (CPF digitado no campo de CNPJ)`}.
              </p>
              <Tabela
                colunas={[
                  ['CNPJ', (l) => <span className="text-nowrap">{formataCnpj(l.cnpj)}</span>],
                  ['Nome', (l) => l.nome || '—'],
                  ['Abordagens', (l) => formataNumero(l.abordagens), true],
                ]}
                linhas={e.mais_abordadas}
              />
            </MDBCardBody></MDBCard>
          </MDBCol>
        </MDBRow>

        <MDBCard className="mb-3 pode-quebrar"><MDBCardBody>
          <h6 className="mb-1">Infrações aplicadas por objeto</h6>
          <p className="small text-muted mb-2">
            {inf.por_natureza.map((n) => `${n.natureza}: ${formataNumero(n.autuadas)} autuadas de ${formataNumero(n.registradas)} ocorrências registradas`).join(' · ')}
            {inf.sem_item > 0 && ` · ${formataNumero(inf.sem_item)} autuada(s) sem objeto lançado`}
          </p>
          <p className="small mb-3">
            <MDBIcon icon="info-circle" className="mr-1 text-muted" />
            A quantidade é a da mercadoria lançada na infração, na unidade lançada — <strong>não é a carga total do veículo</strong>.
          </p>
          {inf.volume_por_unidade.length > 0 && (
            <div className="d-flex flex-wrap mb-3" style={{ gap: '.5rem' }}>
              {inf.volume_por_unidade.map((u) => (
                <span key={u.unidade} className="badge badge-light border" style={{ fontSize: '.85rem', fontWeight: 500 }}>
                  {formataQuantidade(u.quantidade)} {u.unidade}
                </span>
              ))}
            </div>
          )}
          <TabelaObjetos grupos={inf.por_grupo} />
          <small className="text-muted d-block mt-2">
            Uma infração com objetos de grupos diferentes aparece em cada grupo; por isso a coluna não soma o total de infrações.
          </small>
        </MDBCardBody></MDBCard>

        <MDBRow className="mb-3">
          <MDBCol md="5" className="mb-2">
            <MDBCard className="h-100"><MDBCardBody>
              <h6 className="mb-3">Ocorrências por razão da autuação</h6>
              <Tabela
                colunas={[
                  ['Razão', (l) => `${l.razao} (${l.natureza})`],
                  ['Registradas', (l) => formataNumero(l.ocorrencias), true],
                  ['Autuadas', (l) => formataNumero(l.autuadas), true],
                ]}
                linhas={inf.por_razao}
                rodape={['Total', formataNumero(inf.registradas), formataNumero(inf.autuadas)]}
              />
            </MDBCardBody></MDBCard>
          </MDBCol>
          <MDBCol md="7" className="mb-2">
            <MDBCard className="h-100"><MDBCardBody>
              <h6 className="mb-1">Valores dos autos de infração <small className="text-muted">(Receita, por data de lavratura)</small></h6>
              <p className="small text-muted mb-2">
                {dados.carga && `Última carga: ${formataDataHora(dados.carga).slice(0, 10)}. `}
                {textoVinculo(rec.vinculo)}
              </p>
              {rec.incompleta && (
                <p className="small mb-3" style={alerta}>
                  <MDBIcon icon="exclamation-triangle" className="mr-1" />
                  {avisoReceita(rec)}
                </p>
              )}
              {notaForaDosPostos(rec) && (
                <p className="small mb-3" style={alerta}>
                  <MDBIcon icon="exclamation-triangle" className="mr-1" />
                  {notaForaDosPostos(rec)}
                </p>
              )}
              <Tabela
                colunas={[
                  ['Autos', (l) => formataNumero(l.autos), true],
                  ['Imposto', (l) => formataReaisCheio(l.imposto), true],
                  ['Multa', (l) => formataReaisCheio(l.multa), true],
                  ['Multa formal', (l) => formataReaisCheio(l.multa_formal), true],
                  ['Total', (l) => <strong>{formataReaisCheio(l.total)}</strong>, true],
                ]}
                linhas={[rec]}
              />
              <h6 className="mt-3 mb-2" style={{ fontSize: '.9rem' }}>Por origem (RF de lavratura)</h6>
              <Tabela
                colunas={[
                  ['Origem', (l) => (l.posto ? l.origem : <em>{`${l.origem} (não é posto)`}</em>)],
                  ['Autos', (l) => formataNumero(l.autos), true],
                  ['Total', (l) => formataReaisCheio(l.total), true],
                  ['% do valor', (l) => (rec.total ? `${((100 * l.total) / rec.total).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : '—'), true],
                ]}
                linhas={rec.por_origem}
                rodape={['Total', formataNumero(rec.autos), formataReaisCheio(rec.total), '']}
              />
              {rec.postos_sem_autos.length > 0 && (
                <small className="text-muted d-block mt-2">
                  {notaSemAutos(rec.postos_sem_autos, rec.incompleta ? 1 : 0)}
                </small>
              )}
              <h6 className="mt-3 mb-2" style={{ fontSize: '.9rem' }}>Infrações que mais pesam (valor)</h6>
              <ListaBarras
                topN={8}
                formata={formataReais}
                itens={rec.por_infracao.map((i) => ({
                  rotulo: rotuloInfracao(i), n: i.total, outra: `${formataNumero(i.autos)} autos`,
                }))}
                destaque={(i) => i.outra || null}
              />
            </MDBCardBody></MDBCard>
          </MDBCol>
        </MDBRow>
      </>
    );
  };

  return (
    <>
      <NavBar />
      <style>{CSS_IMPRESSAO}</style>
      <MDBContainer className="my-4 consolidado-fiscalizacao">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-0">Consolidado da Fiscalização</h4>
            {dados && (
              <small className="text-muted">
                {rotuloPeriodo(dados.periodo)}
                {dados.posto ? ` · ${dados.posto}` : ' · todos os postos'}
              </small>
            )}
          </div>
          <div className="painel-acoes d-print-none">
            <NotaMetodologica itens={NOTA} />
            <button
              type="button"
              className="painel-acao"
              onClick={() => window.print()}
              disabled={!dados || carregando}
              title="Imprime a tela inteira; para PDF, escolha &quot;Salvar como PDF&quot; como impressora"
            >
              <MDBIcon icon="print" />
              Imprimir / PDF
            </button>
            <button type="button" className="painel-acao" onClick={exportar} disabled={!dados || carregando || exportando}>
              <MDBIcon icon={exportando ? 'spinner' : 'file-excel'} spin={exportando} />
              {exportando ? 'Gerando…' : 'Exportar XLSX'}
            </button>
            <button type="button" className="painel-acao" onClick={atualizar} disabled={carregando}>
              <MDBIcon icon="refresh" spin={carregando} />
              {carregando ? 'Atualizando…' : 'Atualizar'}
            </button>
          </div>
        </div>
        <div className="d-print-none">{filtrosForm}</div>
        {corpo()}
        {dados && (
          <div className="so-impressao" style={{ display: 'none', fontSize: '8.5pt' }}>
            <h6 className="mt-3 mb-2">Como ler estes números</h6>
            {NOTA.map(({ titulo, texto }) => (
              <p key={titulo} className="mb-1"><strong>{titulo}:</strong> {texto}</p>
            ))}
            <p className="text-muted mt-2 mb-0">
              SIG · Consolidado da Fiscalização · impresso em {agoraBR()}
            </p>
          </div>
        )}
      </MDBContainer>
    </>
  );
}
